import { WorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Steps } from 'antd';
import { useIntl } from 'react-intl';
import { useWorkorderItems } from '..';
import SelectPartList from '../SelectPartList';
import SelectServiceList from '../SelectServiceList';
import SelectWorkOrderItemType from '../SelectWorkOrderItemType';
import WorkOrderSetupItems from '../WorkOrderSetupItems';

import './styles.less';
import classNames from 'classnames';
import SelectKitList from '../SelectKitList';
import { useState } from 'react';
import PartsFragaCreate from '../PartsFragaCreate';

type StepsItem = {
  title: string;
  description: string;
  content: React.ReactNode;
};

type Props = {
  currentStep: number;
  workorder?: WorkOrder;
  onCancel: VoidFunction;
};

export enum PartListType {
  PARTS_SYSTEM = 'PARTS_SYSTEM',
  PARTS_FRAGA = 'PARTS_FRAGA',
}

export enum ServiceListType {
  System = 'system',
  Executed = 'executed',
}

const WorkOrderSteps: React.FC<Props> = ({
  currentStep,
  workorder,
  onCancel,
}) => {
  const intl = useIntl().formatMessage;
  const { itemType } = useWorkorderItems();

  const [partListType, setPartListType] = useState<PartListType>(
    PartListType.PARTS_SYSTEM,
  );

  const [serviceListType, setServiceListType] = useState<ServiceListType>(
    ServiceListType.System,
  );

  const componentItemList = {
    [WorkOrderItemType.Part]: (
      <SelectPartList
        onCancel={onCancel}
        partListType={partListType}
        setPartListType={setPartListType}
      />
    ),
    [WorkOrderItemType.Service]: (
      <SelectServiceList
        serviceListType={serviceListType}
        setServiceListType={setServiceListType}
        onCancel={onCancel}
        key={currentStep}
      />
    ),
    [WorkOrderItemType.Kit]: <SelectKitList onCancel={onCancel} />,
    [WorkOrderItemType.Undefined]: null,
    [WorkOrderItemType.Benefit]: null,
    [WorkOrderItemType.Bosch]: null,
    [WorkOrderItemType.Porto]: null,
  };

  const steps: (StepsItem | null)[] = [
    {
      title: intl({ id: 'workOrders.details.items.add.select' }),
      description: intl({
        id: 'workOrders.details.items.add.select.description',
      }),
      content: <SelectWorkOrderItemType onCancel={onCancel} />,
    },
    {
      title: intl({ id: 'workOrders.details.items.add.search' }),
      description: intl({
        id: 'workOrders.details.items.add.search.description',
      }),
      content: !!workorder && itemType && componentItemList[itemType],
    },
    partListType === PartListType.PARTS_FRAGA
      ? {
          title: intl({ id: 'workOrders.details.items.add.createParts' }),
          description: intl({
            id: 'workOrders.details.items.add.createParts.description',
          }),
          content: (
            <PartsFragaCreate
              onCancel={() => {
                onCancel();
                setPartListType(PartListType.PARTS_SYSTEM);
              }}
              key={currentStep === 2 ? 1 : 0}
            />
          ),
        }
      : null,
    {
      title: intl({ id: 'workOrders.details.items.add.setupItems' }),
      description: intl({
        id: 'workOrders.details.items.add.setupItems.description',
      }),
      content: (
        <WorkOrderSetupItems
          onCancel={() => {
            onCancel();
            setPartListType(PartListType.PARTS_SYSTEM);
          }}
          onPrev={() => setPartListType(PartListType.PARTS_SYSTEM)}
          partListType={partListType}
          key={currentStep === 2 ? 1 : 0}
        />
      ),
    },
  ];

  const stepFiltered = steps.filter((step) => !!step) as StepsItem[];

  return (
    <div className="workorder-steps">
      <Steps current={currentStep}>
        {stepFiltered.map((step, key) => (
          <Steps.Step
            key={key}
            title={step.title}
            description={step.description}
          />
        ))}
      </Steps>

      <div className="workorder-steps-content">
        {stepFiltered.map((step, index) => (
          <div
            className={classNames('step-content', {
              'step-active': index === currentStep,
            })}
            key={index}
          >
            {step.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkOrderSteps;
